import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Loader2, Download } from "lucide-react";
import { api } from "@/lib/api";
import { fmtEUR, fmtDate } from "@/constants";
import { Button } from "@/components/ui/button";

const Badge = ({ ok, label }) => (
  <span className={`inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase ${ok ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>{label}</span>
);

export default function InvoicesSection({ rid }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.get(`/reservations/${rid}/invoices`).then((r) => setData(r.data)).catch(() => {});
  }, [rid]);
  useEffect(() => { load(); }, [load]);

  const gen = async (kind) => {
    setBusy(kind);
    try {
      const r = await api.post(`/reservations/${rid}/invoices`, { kind });
      toast.success(`Facture ${r.data.number} générée — prête pour Indy`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setBusy("");
    }
  };

  if (!data) return null;
  const s = data.status;
  return (
    <section data-testid="invoices-section" className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 space-y-4">
      <h2 className="font-heading text-lg font-bold">Facturation</h2>
      <div className="flex flex-wrap gap-1.5" data-testid="billing-statuses">
        <Badge ok={s.acompte_recu} label={s.acompte_recu ? "Acompte reçu" : "Acompte non reçu"} />
        <Badge ok={s.facture_acompte} label={s.facture_acompte ? "Facture acompte générée" : "Facture acompte non générée"} />
        <Badge ok={s.solde_recu} label={s.solde_recu ? "Solde reçu" : "Solde non reçu"} />
        <Badge ok={s.facture_solde} label={s.facture_solde ? "Facture solde générée" : "Facture solde non générée"} />
        {s.termine && <span className="inline-block rounded-full px-2.5 py-1 text-[10px] font-bold uppercase bg-emerald-600 text-white" data-testid="billing-status-done">Facturation terminée</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="invoice-gen-acompte" onClick={() => gen("acompte")} disabled={busy === "acompte" || !s.acompte_recu}
          className="bg-sky-600 hover:bg-sky-700 text-white" title={!s.acompte_recu ? "Enregistrez d'abord l'acompte dans Paiements" : ""}>
          {busy === "acompte" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          Générer facture d'acompte
        </Button>
        <Button data-testid="invoice-gen-solde" onClick={() => gen("solde")} disabled={busy === "solde" || !s.solde_recu}
          className="bg-emerald-600 hover:bg-emerald-700 text-white" title={!s.solde_recu ? "Enregistrez d'abord le solde dans Paiements" : ""}>
          {busy === "solde" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
          Générer facture de solde
        </Button>
      </div>
      {data.invoices.length > 0 && (
        <ul className="divide-y divide-slate-100" data-testid="invoices-list">
          {data.invoices.map((inv, i) => (
            <li key={inv.id} data-testid={`invoice-line-${i}`} className="py-2 flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-slate-400 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="font-semibold">{inv.number}</span>
                <span className="text-slate-500"> — {inv.kind === "acompte" ? "Acompte" : "Solde"} · {fmtEUR(inv.amount)} · {fmtDate(inv.date)}</span>
              </span>
              <a data-testid={`invoice-dl-${i}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/reservations/${rid}/invoices/${inv.id}/pdf`}
                target="_blank" rel="noopener noreferrer"
                className="shrink-0 inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 text-sky-700 px-2.5 py-1 text-[10px] font-bold uppercase hover:bg-sky-100 transition-colors">
                <Download className="h-3 w-3" /> PDF
              </a>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-slate-400">Factures au format PDF structuré (TVA non applicable, art. 293 B du CGI), prêtes à importer dans Indy.</p>
    </section>
  );
}
